/**
 * Zustand store for Swap data
 */
import { create } from "zustand";
import { devtools } from "zustand/middleware";

const SWAP_SETTINGS_KEY = "khays_swap_settings";
const SWAP_PRICING_KEY = "khays_swap_pricing";
const SWAP_TRADEIN_PRICING_KEY = "khays_swap_tradein_pricing";
const SWAP_CONDITION_TEMPLATES_KEY = "khays_swap_condition_templates";
const SWAP_NOTES_KEY = "khays_swap_notes";

// ─── Types ─────────────────────────────────────────────────────

export interface SwapSettings {
  enabled: boolean;
  pageLabel: string;
  showNewDevicePrice: boolean;
  showTradeInValue: boolean;
  showPriceDifference: boolean;
  valuationMode: "admin_prices" | "percentage_deductions" | "manual_quote" | "all";
  whatsappNumber: string;
  whatsappMessage: string;
  percentageDeductionBase: number;
  batteryDeductions: Record<string, number>;
  screenDeductions: Record<string, number>;
  bodyDeductions: Record<string, number>;
  headerColor?: string;
  headerTextColor?: string;
  headerEffect?: "none" | "gradient" | "glow" | "shimmer";
  headerGradientEnd?: string;
  buttonColor?: string;
  buttonTextColor?: string;
  buttonEffect?: "none" | "gradient" | "glow" | "shimmer";
  buttonGradientEnd?: string;
  swapShippingDisabledCategories: string[];
  swapShippingAllowedOptions: Record<string, string[]>;
  swapDisabledCategories: string[];
  storefrontDisabledCategories: string[];
  showConditionDescriptions?: boolean;
  swapCurrentSubVariantsEnabled?: boolean;
  swapCurrentScreenConditionsEnabled?: boolean;
  conditionCategoryAssignments?: Record<string, string[]>;
  conditionDeviceAssignments?: Record<string, string[]>;
  conditionGradeMap?: Record<string, string>;
  conditionGradingEnabled?: boolean;
  conditionGradingMode?: "auto" | "manual";
  conditionGradeExplanations?: Record<string, string>;
}

export interface SwapConditionTemplate {
  id: string;
  label: string;
  category: "battery" | "screen" | "body" | "general";
  options: SwapConditionOption[];
  multiSelect?: boolean;
  maxSelections?: number;
  required?: boolean;
  sortOrder?: number;
}

export interface SwapConditionOption {
  id: string;
  label: string;
  deductionPercent: number;
  description?: string;
  sortOrder?: number;
  group?: string; // Options in the same group are mutually exclusive within a multi-select template
}

// ─── Defaults ──────────────────────────────────────────────────

function getDefaultSettings(): SwapSettings {
  return {
    enabled: false, pageLabel: "Swapping", showNewDevicePrice: true, showTradeInValue: true,
    showPriceDifference: true, valuationMode: "all", whatsappNumber: "",
    whatsappMessage: "Hi, I'd like to swap my device.\n\n{details}",
    percentageDeductionBase: 100, batteryDeductions: {}, screenDeductions: {}, bodyDeductions: {},
    headerColor: "", headerTextColor: "", headerEffect: "none", headerGradientEnd: "",
    buttonColor: "", buttonTextColor: "", buttonEffect: "none", buttonGradientEnd: "",
    swapShippingDisabledCategories: [], swapShippingAllowedOptions: {},
    swapDisabledCategories: [], storefrontDisabledCategories: [],
    showConditionDescriptions: true, swapCurrentSubVariantsEnabled: true, swapCurrentScreenConditionsEnabled: true,
    conditionCategoryAssignments: {}, conditionDeviceAssignments: {},
    conditionGradeMap: {}, conditionGradingEnabled: false, conditionGradingMode: "auto", conditionGradeExplanations: {},
  };
}

function getDefaultConditionTemplates(): SwapConditionTemplate[] {
  return [
    {
      id: "battery_health", label: "Battery Health", category: "battery", multiSelect: false, maxSelections: 1, required: true, sortOrder: 0,
      options: [
        { id: "bat_excellent", label: "90-100%", deductionPercent: 0, description: "Excellent battery health, holds charge well throughout the day", sortOrder: 0 },
        { id: "bat_good", label: "80-89%", deductionPercent: 5, description: "Good battery, may need charging once during heavy use", sortOrder: 1 },
        { id: "bat_fair", label: "70-79%", deductionPercent: 10, description: "Fair battery, noticeably shorter battery life", sortOrder: 2 },
        { id: "bat_poor", label: "Below 70%", deductionPercent: 20, description: "Poor battery, needs frequent charging or replacement", sortOrder: 3 },
      ],
    },
    {
      id: "screen_condition", label: "Screen Condition", category: "screen", multiSelect: false, maxSelections: 1, required: true, sortOrder: 1,
      options: [
        { id: "scr_perfect", label: "Perfect", deductionPercent: 0, description: "No scratches, marks or blemishes visible on screen", sortOrder: 0 },
        { id: "scr_minor", label: "Minor scratches", deductionPercent: 5, description: "Light surface scratches only visible under direct light", sortOrder: 1 },
        { id: "scr_visible", label: "Visible scratches", deductionPercent: 15, description: "Clearly visible marks or scratches during normal use", sortOrder: 2 },
        { id: "scr_cracked", label: "Cracked", deductionPercent: 35, description: "Screen has cracks, chips or broken glass", sortOrder: 3 },
      ],
    },
    {
      id: "body_condition", label: "Body Condition", category: "body", multiSelect: false, maxSelections: 1, required: true, sortOrder: 2,
      options: [
        { id: "body_mint", label: "Mint", deductionPercent: 0, description: "Like new condition, no visible wear or marks", sortOrder: 0 },
        { id: "body_good", label: "Good", deductionPercent: 5, description: "Minor wear, small cosmetic marks barely noticeable", sortOrder: 1 },
        { id: "body_fair", label: "Fair", deductionPercent: 10, description: "Visible wear, scuffs or dents on body or edges", sortOrder: 2 },
        { id: "body_poor", label: "Poor", deductionPercent: 25, description: "Heavy wear, significant dents, deep scratches or damage", sortOrder: 3 },
      ],
    },
  ];
}

// ─── Zustand Store ────────────────────────────────────────────

interface SwapState {
  settings: SwapSettings;
  pricing: Record<string, number>;
  tradeInPricing: Record<string, number>;
  templates: SwapConditionTemplate[];
  notes: string;
  _rev: number;
  setSettings: (s: SwapSettings) => void;
  setPricing: (p: Record<string, number>) => void;
  setTradeInPricing: (p: Record<string, number>) => void;
  setTemplates: (t: SwapConditionTemplate[]) => void;
  setNotes: (n: string) => void;
  reload: () => void;
}

function loadSettings(): SwapSettings {
  try { const s = localStorage.getItem(SWAP_SETTINGS_KEY); if (s) return { ...getDefaultSettings(), ...JSON.parse(s) }; } catch {}
  return getDefaultSettings();
}
function loadPricing(): Record<string, number> {
  try { const s = localStorage.getItem(SWAP_PRICING_KEY); if (s) return JSON.parse(s); } catch {} return {};
}
function loadTradeIn(): Record<string, number> {
  try { const s = localStorage.getItem(SWAP_TRADEIN_PRICING_KEY); if (s) return JSON.parse(s); } catch {} return {};
}
function loadTemplates(): SwapConditionTemplate[] {
  try {
    const s = localStorage.getItem(SWAP_CONDITION_TEMPLATES_KEY);
    if (s) {
      const templates = JSON.parse(s) as SwapConditionTemplate[];
      templates.forEach((t, i) => { if (t.sortOrder === undefined) t.sortOrder = i; t.options.forEach((o, j) => { if (o.sortOrder === undefined) o.sortOrder = j; }); });
      return templates.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    }
  } catch {}
  return getDefaultConditionTemplates();
}
function loadNotes(): string {
  try {
    const raw = localStorage.getItem(SWAP_NOTES_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map((l: any) => typeof l === "string" ? l : l.text || "").join("\n");
      if (typeof parsed === "string") return parsed;
    }
  } catch {}
  return "";
}

export const useSwapStore = create<SwapState>()(
  devtools(
    (set, get) => ({
      settings: loadSettings(),
      pricing: loadPricing(),
      tradeInPricing: loadTradeIn(),
      templates: loadTemplates(),
      notes: loadNotes(),
      _rev: 0,
      setSettings: (s) => { localStorage.setItem(SWAP_SETTINGS_KEY, JSON.stringify(s)); set({ settings: s, _rev: get()._rev + 1 }); },
      setPricing: (p) => { localStorage.setItem(SWAP_PRICING_KEY, JSON.stringify(p)); set({ pricing: p, _rev: get()._rev + 1 }); },
      setTradeInPricing: (p) => { localStorage.setItem(SWAP_TRADEIN_PRICING_KEY, JSON.stringify(p)); set({ tradeInPricing: p, _rev: get()._rev + 1 }); },
      setTemplates: (t) => { localStorage.setItem(SWAP_CONDITION_TEMPLATES_KEY, JSON.stringify(t)); set({ templates: t, _rev: get()._rev + 1 }); },
      setNotes: (n) => { localStorage.setItem(SWAP_NOTES_KEY, JSON.stringify(n)); set({ notes: n, _rev: get()._rev + 1 }); },
      reload: () => set({ settings: loadSettings(), pricing: loadPricing(), tradeInPricing: loadTradeIn(), templates: loadTemplates(), notes: loadNotes(), _rev: get()._rev + 1 }),
    }),
    { name: "SwapStore" }
  )
);

// ─── Backward-compatible exports ──────────────────────────────

export function loadSwapSettings(): SwapSettings { return useSwapStore.getState().settings; }
export function saveSwapSettings(settings: SwapSettings): void { useSwapStore.getState().setSettings(settings); }

export function loadSwapConditionTemplates(): SwapConditionTemplate[] { return useSwapStore.getState().templates; }
export function saveSwapConditionTemplates(templates: SwapConditionTemplate[]): void { useSwapStore.getState().setTemplates(templates); }

export function loadSwapPricing(): Record<string, number> { return useSwapStore.getState().pricing; }
export function saveSwapPricing(pricing: Record<string, number>): void { useSwapStore.getState().setPricing(pricing); }

export function loadSwapTradeInPricing(): Record<string, number> { return useSwapStore.getState().tradeInPricing; }
export function saveSwapTradeInPricing(pricing: Record<string, number>): void { useSwapStore.getState().setTradeInPricing(pricing); }

export function loadSwapNotes(): string { return useSwapStore.getState().notes; }
export function saveSwapNotes(notes: string): void { useSwapStore.getState().setNotes(notes); }

export function getSwapPriceKey(device: string, variant: string, grade?: string, screen?: string, subVariant?: string): string {
  const parts = [device, variant];
  if (grade) parts.push(grade);
  if (screen) parts.push(screen);
  if (subVariant) parts.push(subVariant);
  return parts.join("|");
}

export function getSwapPrice(key: string): number | null {
  const p = loadSwapPricing();
  return p[key] !== undefined ? p[key] : null;
}

export function setSwapPrice(key: string, price: number): void {
  const p = { ...loadSwapPricing(), [key]: price };
  saveSwapPricing(p);
}

export function getTradeInPrice(key: string): number | null {
  const p = loadSwapTradeInPricing();
  return p[key] !== undefined ? p[key] : null;
}

export function setTradeInPrice(key: string, price: number): void {
  const p = { ...loadSwapTradeInPricing(), [key]: price };
  saveSwapTradeInPricing(p);
}

export function calculateTradeInValue(
  basePrice: number, conditionSelections: Record<string, string[]>, templates: SwapConditionTemplate[]
): number {
  let totalDeduction = 0;
  for (const [templateId, optionIds] of Object.entries(conditionSelections)) {
    const template = templates.find(t => t.id === templateId);
    if (!template) continue;
    for (const optionId of optionIds) {
      const option = template.options.find(o => o.id === optionId);
      if (option) totalDeduction += option.deductionPercent;
    }
  }
  return Math.round(basePrice * Math.max(0, 100 - totalDeduction) / 100);
}

export function isSwapCategoryEnabled(category: string): boolean {
  return !(loadSwapSettings().swapDisabledCategories || []).includes(category);
}

export function isStorefrontCategoryEnabled(category: string): boolean {
  return !(loadSwapSettings().storefrontDisabledCategories || []).includes(category);
}

export function isSwapShippingEnabled(category: string): boolean {
  return !(loadSwapSettings().swapShippingDisabledCategories || []).includes(category);
}

export function getSwapAllowedShippingOptions(category: string): string[] | null {
  const allowed = (loadSwapSettings().swapShippingAllowedOptions || {})[category];
  return allowed && allowed.length > 0 ? allowed : null;
}

export function isConditionForCategory(templateId: string, category: string): boolean {
  const assigned = (loadSwapSettings().conditionCategoryAssignments || {})[templateId];
  if (!assigned || assigned.length === 0) return true;
  return assigned.includes(category);
}

export function isConditionForDevice(templateId: string, deviceName: string): boolean {
  const assigned = (loadSwapSettings().conditionDeviceAssignments || {})[templateId];
  if (!assigned || assigned.length === 0) return true;
  return assigned.includes(deviceName);
}

export function getApplicableConditions(templates: SwapConditionTemplate[], category: string, deviceName: string): SwapConditionTemplate[] {
  return templates.filter(t => isConditionForCategory(t.id, category) && isConditionForDevice(t.id, deviceName));
}

// ─── Auto-grading ─────────────────────────────────────────────

export function determineAutoGrade(
  conditionSelections: Record<string, string[]>,
  templates: SwapConditionTemplate[],
  settings: SwapSettings
): { grade: string; explanation: string } | null {
  if (!settings.conditionGradingEnabled) return null;
  const gradeMap = settings.conditionGradeMap || {};
  const explanations = settings.conditionGradeExplanations || {};

  const matchesSelections = (savedSelections: Record<string, string[]>): boolean => {
    if (Object.keys(savedSelections).length === 0) return false;
    return Object.entries(savedSelections).every(([tmplId, optIds]) => {
      const customerOpts = conditionSelections[tmplId] || [];
      return optIds.every(id => customerOpts.includes(id));
    });
  };

  // 1. Condition chains
  const chainKeys = Object.keys(gradeMap).filter(k => k.startsWith("__chain_") && !k.startsWith("__chainsel_") && !k.startsWith("__chainname_"));
  for (const chainKey of chainKeys) {
    const selKey = `__chainsel_${chainKey}`;
    const rawSelections = gradeMap[selKey];
    if (!rawSelections) continue;
    try {
      const chainSelections: Record<string, string[]> = typeof rawSelections === "string" ? JSON.parse(rawSelections) : {};
      if (matchesSelections(chainSelections)) {
        const grade = gradeMap[chainKey];
        return { grade, explanation: explanations[grade] || `Based on your device condition, your device is graded as ${grade}.` };
      }
    } catch {}
  }

  // 2. Custom mappings
  if (settings.conditionGradingMode === "manual") {
    const customKeys = Object.keys(gradeMap).filter(k => k.startsWith("__custom_") && !k.startsWith("__sel_"));
    for (const mappingKey of customKeys) {
      const selectionsKey = `__sel_${mappingKey}`;
      const rawSelections = gradeMap[selectionsKey];
      if (!rawSelections) continue;
      try {
        const mappedSelections: Record<string, string[]> = typeof rawSelections === "string" ? JSON.parse(rawSelections) : {};
        if (matchesSelections(mappedSelections)) {
          const grade = gradeMap[mappingKey];
          return { grade, explanation: explanations[grade] || `Based on your device condition, your device is graded as ${grade}.` };
        }
      } catch {}
    }
  }

  // 3. Deduction-based fallback
  let maxDeduction = 0;
  for (const [templateId, optionIds] of Object.entries(conditionSelections)) {
    const template = templates.find(t => t.id === templateId);
    if (!template) continue;
    for (const optionId of optionIds) {
      const option = template.options.find(o => o.id === optionId);
      if (option && option.deductionPercent > maxDeduction) maxDeduction = option.deductionPercent;
    }
  }

  for (const [key, grade] of Object.entries(gradeMap)) {
    const match = key.match(/^__deduction_(\d+)-(\d+)$/);
    if (match) {
      const [, min, max] = match.map(Number);
      if (maxDeduction >= min && maxDeduction <= max) {
        return { grade, explanation: explanations[grade] || `Your device condition indicates a ${grade} grade.` };
      }
    }
  }

  const defaultGrade = maxDeduction <= 5 ? "Grade A" : maxDeduction <= 15 ? "Grade B" : maxDeduction <= 25 ? "Grade C" : "Grade D";
  return { grade: defaultGrade, explanation: explanations[defaultGrade] || `Your device condition indicates a ${defaultGrade} grade based on the assessed conditions.` };
}
